import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton } from "@/components/common/EmptyState";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const SCALE_LABELS = {
  1: "Fails to meet expectations",
  2: "Basic, inconsistent execution",
};
const scaleFor = (max) => {
  const labels = { ...SCALE_LABELS, [Math.ceil(max / 2)]: "Meets expectations", [max - 1]: "Strong, independent, quality output", [max]: "Exceptional; mentors others" };
  return Array.from({ length: max }, (_, i) => ({ v: i + 1, label: labels[i + 1] || `${i + 1} / ${max}` }));
};

const REVIEWER_TITLE = { self: "Self", manager: "Manager", hr: "HR", peer: "Peer" };

export default function ReviewForm() {
  const { cycleId, employeeId, reviewerType } = useParams();
  const navigate = useNavigate();
  const { me } = useApp();
  const { data: employee, isLoading: le } = useQuery({ queryKey: ["employee", employeeId], queryFn: () => get(`/hrms/employees/${employeeId}`) });
  const { data: template, isLoading: lt } = useQuery({ queryKey: ["employee-template", employeeId], queryFn: () => get(`/hrms/performance/employee-template/${employeeId}`), enabled: !!employeeId });
  const { data: cycles = [] } = useQuery({ queryKey: ["perf-cycles"], queryFn: () => get("/hrms/performance/cycles") });
  const cycle = cycles.find((c) => c.id === cycleId);

  const [scores, setScores] = useState({});
  const [feedback, setFeedback] = useState("");
  useEffect(() => {
    if (template) setScores((s) => Object.fromEntries(template.dimensions.map((d) => [d.id, s[d.id] || Math.ceil((d.max_score || 5) / 2)])));
  }, [template]);

  const submit = useMutation({
    mutationFn: (body) => post("/hrms/performance/reviews", body),
    onSuccess: () => { toast.success("Review submitted"); navigate(`/hrms/performance/${cycleId}/${employeeId}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not submit review"),
  });

  if (le || lt || !template) return <Skeleton className="h-96" />;

  const grouped = template.dimensions.reduce((acc, d) => { (acc[d.category] ||= []).push(d); return acc; }, {});
  const total = Object.values(scores).reduce((a, b) => a + b, 0);
  const max = template.dimensions.reduce((a, d) => a + (d.max_score || 5), 0);

  const go = () => {
    submit.mutate({
      cycle_id: cycleId, employee_id: employeeId, reviewer_id: me?.id, reviewer_type: reviewerType,
      ratings: template.dimensions.map((d) => ({ dimension_id: d.id, score: scores[d.id] })),
      feedback_text: feedback,
    });
  };

  return (
    <div className="max-w-3xl">
      <PageHeader title={`${REVIEWER_TITLE[reviewerType] || reviewerType} Review`}
        subtitle={`${employee.name} · ${cycle?.name || cycleId} · Running total: ${total} / ${max}`} />

      {Object.entries(grouped).map(([category, dims]) => (
        <Section key={category} title={category} className="mb-4">
          <div className="divide-y divide-border">
            {dims.map((d) => {
              const scale = scaleFor(d.max_score || 5);
              return (
                <div key={d.id} className="px-4 py-3">
                  <div className="mb-2 flex items-center justify-between text-sm font-medium">
                    <span>{d.name}</span>
                    <span className="text-xs font-normal text-muted-foreground">out of {d.max_score || 5}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {scale.map((s) => (
                      <button key={s.v} type="button" data-testid={`rate-${d.id}-${s.v}`} title={s.label}
                        onClick={() => setScores((sc) => ({ ...sc, [d.id]: s.v }))}
                        className={cn("flex h-8 w-8 items-center justify-center rounded-md border text-sm font-medium",
                          scores[d.id] === s.v ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:bg-muted")}>
                        {s.v}
                      </button>
                    ))}
                    <span className="ml-2 self-center text-xs text-muted-foreground">{scale.find((s) => s.v === scores[d.id])?.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      ))}

      <Section title="Written Feedback" className="mb-4">
        <div className="p-4">
          <Label>Feedback</Label>
          <Textarea data-testid="review-feedback" className="mt-1" rows={4} value={feedback} onChange={(e) => setFeedback(e.target.value)} />
        </div>
      </Section>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => navigate(-1)}>Cancel</Button>
        <Button data-testid="submit-review" onClick={go} disabled={submit.isPending}>Submit Review ({total}/{max})</Button>
      </div>
    </div>
  );
}
